import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Mail, FileText, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useConference } from '@/context/ConferenceContext';
import { toast } from 'sonner';

const SponsorsPage = () => {
  const { shortName, importantDetails, loading: confLoading, siteConfig } = useConference();
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSponsors = async () => {
      if (confLoading) return;
      
      setLoading(true);
      try {
        // Use empty array since API doesn't exist
        setSponsors([]);
      } catch (error) {
        console.error('Failed to fetch sponsors:', error);
        setSponsors([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSponsors();
  }, [shortName, confLoading]);

  const sponsorTiers = [
    { tier: 'platinum', title: 'Platinum Sponsors', color: 'from-sky-400 to-sky-500' },
    { tier: 'gold', title: 'Gold Sponsors', color: 'from-amber-400 to-amber-500' },
    { tier: 'silver', title: 'Silver Sponsors', color: 'from-gray-300 to-gray-400' },
    { tier: 'exhibitor', title: 'Exhibitors', color: 'from-purple-400 to-purple-500' },
    { tier: 'partner', title: 'Partners', color: 'from-gray-400 to-gray-500' },
  ];

  if (loading || confLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-sky-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading sponsors...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Sponsors - {importantDetails?.ConferenceTitle || siteConfig?.name || 'Conference'}</title>
        <meta name="description" content="Our sponsors and partners for CCAI-2026" />
      </Helmet>

      <div className="bg-white min-h-screen">
        {/* Hero */}
        <section className="hero-gradient py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Our Sponsors</h1>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Thank you to our sponsors for making CCAI-2026 possible
              </p>
            </motion.div>
          </div>
        </section>

        {/* Sponsor Tiers */}
        {sponsorTiers.map((tierInfo, tierIndex) => {
          const tierSponsors = sponsors.filter((s) => s.sponsorType === tierInfo.tier);
          if (tierSponsors.length === 0) return null;

          return (
            <section key={tierInfo.tier} className="section-spacing">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-8">
                  <div className={`inline-block px-6 py-2 rounded-full bg-gradient-to-r ${tierInfo.color} text-white font-semibold mb-4`}>
                    {tierInfo.title}
                  </div>
                </div>

                <div
                  className={`grid gap-8 ${
                    tierInfo.tier === 'platinum'
                      ? 'grid-cols-1 md:grid-cols-2'
                      : tierInfo.tier === 'gold'
                      ? 'grid-cols-1 md:grid-cols-3'
                      : 'grid-cols-2 md:grid-cols-4'
                  }`}
                >
                  {tierSponsors.map((sponsor, index) => (
                    <motion.div
                      key={sponsor.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className="card-hover bg-white p-8 rounded-2xl border-2 border-gray-100 shadow-sm flex flex-col items-center justify-center text-center"
                      data-testid={`sponsor-${sponsor.id}`}
                    >
                      <a
                        href={sponsor.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                      >
                        <img
                          src={sponsor.logo}
                          alt={sponsor.name}
                          className="max-h-24 object-contain mb-4 grayscale hover:grayscale-0 transition-all"
                        />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{sponsor.name}</h3>
                        <p className="text-sm text-gray-600">{sponsor.description}</p>
                      </a>
                    </motion.div>
                  ))}
                </div>
              </div>
            </section>
          );
        })}

        {/* Sponsorship Benefits */}
        <section className="section-spacing bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Sponsorship Benefits</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-2xl shadow-sm">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Platinum</h3>
                <ul className="text-gray-600 space-y-2 text-sm">
                  <li>• Keynote speaking slot</li>
                  <li>• Exhibition booth (3m x 3m)</li>
                  <li>• Logo on all materials</li>
                  <li>• 10 complimentary registrations</li>
                  <li>• Full-page ad in proceedings</li>
                  <li>• Social media recognition</li>
                </ul>
                <p className="mt-4 text-2xl font-bold text-sky-500">$25,000</p>
              </div>

              <div className="bg-white p-6 rounded-2xl shadow-sm">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Gold</h3>
                <ul className="text-gray-600 space-y-2 text-sm">
                  <li>• Exhibition booth (2m x 2m)</li>
                  <li>• Logo on website & materials</li>
                  <li>• 6 complimentary registrations</li>
                  <li>• Half-page ad in proceedings</li>
                  <li>• Social media recognition</li>
                </ul>
                <p className="mt-4 text-2xl font-bold text-amber-500">$15,000</p>
              </div>

              <div className="bg-white p-6 rounded-2xl shadow-sm">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Silver</h3>
                <ul className="text-gray-600 space-y-2 text-sm">
                  <li>• Exhibition booth (2m x 2m)</li>
                  <li>• Logo on website</li>
                  <li>• 3 complimentary registrations</li>
                  <li>• Quarter-page ad in proceedings</li>
                </ul>
                <p className="mt-4 text-2xl font-bold text-gray-500">$8,000</p>
              </div>

              <div className="bg-white p-6 rounded-2xl shadow-sm">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Bronze</h3>
                <ul className="text-gray-600 space-y-2 text-sm">
                  <li>• Logo on website</li>
                  <li>• 2 complimentary registrations</li>
                  <li>• Recognition in materials</li>
                </ul>
                <p className="mt-4 text-2xl font-bold text-gray-400">$5,000</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="section-spacing bg-gradient-to-r from-sky-500 to-sky-600">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Become a Sponsor
            </h2>
            <p className="text-lg text-white/90 mb-8">
              Join industry leaders in supporting CCAI-2026 and gain visibility among 500+ civil engineering professionals
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                size="lg"
                className="bg-white text-sky-600 hover:bg-gray-100"
                data-testid="download-prospectus-btn"
              >
                <FileText size={18} className="mr-2" />
                Download Sponsorship Prospectus
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-sky-600"
                onClick={() => (window.location.href = '/contact')}
                data-testid="contact-us-btn"
              >
                <Mail size={18} className="mr-2" />
                Contact Us
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default SponsorsPage;
